import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { format, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';

import type {
  PurchaseProofPurchaseProofCollectionReadModel,
  PurchaseProofPurchaseProofCollectionReadModelStatus
} from '@r/api/generated/client.schemas.ts';
import { DataTableColumnHeader } from '@r/components/data-table/data-table-column-header.tsx';
import { Badge } from '@r/components/ui/badge.tsx';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ui/tooltip.tsx';
import { cn } from '@r/utils/utils.ts';
import type { DataTableFilterField } from '@r/types/index.ts';
import { UserFullname } from '@r/components/user-fullname.tsx';

/** Formats a purchase-proof creation date for display in French locale. */
export function formatPurchaseProofDate(createdAt: string) {
  const date = new Date(createdAt);
  return isValid(date) ? format(date, 'dd/MM/yyyy', { locale: fr }) : createdAt;
}

/** Displays a truncated comment and exposes its full content in a tooltip. */
function PurchaseProofComment({ comment }: { comment?: string | null }) {
  const { t } = useTranslation();
  if (!comment) return null;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="block max-w-64 cursor-help truncate text-left"
            aria-label={t('purchase.proof.comment.show')}
          >
            {comment}
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-sm whitespace-normal break-words">
          {comment}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const statusStyles: Record<
  PurchaseProofPurchaseProofCollectionReadModelStatus,
  { dotClass: string }
> = {
  pending: { dotClass: 'bg-amber-500' },
  processing: { dotClass: 'bg-blue-500' },
  approved: { dotClass: 'bg-emerald-600' },
  rejected: { dotClass: 'bg-red-500' }
};

/** Displays the translated purchase-proof status with its visual indicator. */
function PurchaseProofStatusBadge({
  status
}: {
  status: PurchaseProofPurchaseProofCollectionReadModelStatus;
}) {
  const { t } = useTranslation();
  const statusStyle = statusStyles[status];

  return (
    <Badge variant="outline" className="gap-1.5 border-muted-foreground/20 font-normal">
      <span className={cn('size-1.5 shrink-0 rounded-full', statusStyle.dotClass)} aria-hidden />
      {t(`purchase.proof.status.${status}`)}
    </Badge>
  );
}

/** Returns the translated filter configuration for the purchase-proofs table. */
export function usePurchaseProofsFilterFields(): DataTableFilterField<PurchaseProofPurchaseProofCollectionReadModel>[] {
  const { t } = useTranslation();

  return [
    {
      label: t('label.purchase.proof.filename'),
      value: 'filename',
      placeholder: t('label.purchase.proof.filename')
    },
    {
      label: t('label.purchase.proof.comment'),
      value: 'comment',
      placeholder: t('label.purchase.proof.comment')
    },
    {
      label: t('label.purchase.proof.created.by'),
      value: 'createdByFullname',
      placeholder: t('label.purchase.proof.created.by')
    },
    {
      label: t('label.purchase.proof.company'),
      value: 'company',
      placeholder: t('label.purchase.proof.company')
    },
    {
      label: t('label.purchase.proof.created.at'),
      value: 'createdAt',
      placeholder: t('label.purchase.proof.created.at'),
      isDateRange: true
    },
    {
      label: t('label.purchase.proof.status'),
      value: 'status',
      options: [
        { label: t('purchase.proof.status.pending'), value: 'pending' },
        { label: t('purchase.proof.status.processing'), value: 'processing' },
        { label: t('purchase.proof.status.approved'), value: 'approved' },
        { label: t('purchase.proof.status.rejected'), value: 'rejected' }
      ]
    }
  ];
}

/** Returns the translated column configuration for the purchase-proofs table. */
export function usePurchaseProofsColumns(): ColumnDef<PurchaseProofPurchaseProofCollectionReadModel>[] {
  const { t } = useTranslation();

  return [
    {
      accessorKey: 'id',
      header: t('label.id'),
      enableSorting: false
    },
    {
      accessorKey: 'filename',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.purchase.proof.filename')} />
      ),
      enableSorting: true,
      enableHiding: false
    },
    {
      accessorKey: 'comment',
      header: t('label.purchase.proof.comment'),
      enableSorting: false,
      cell: ({ row }) => <PurchaseProofComment comment={row.original.comment} />
    },
    {
      id: 'createdByFullname',
      accessorFn: (proof) => proof.createdBy?.user.fullname ?? '',
      header: t('label.purchase.proof.created.by'),
      enableSorting: false,
      cell: ({ row }) => (
        <UserFullname
          fullname={row.original.createdBy?.user.fullname ?? ''}
          isDeleted={row.original.createdBy?.user.isDeleted}
        />
      )
    },
    {
      id: 'company',
      accessorFn: (proof) => proof.createdBy?.company?.name ?? '',
      header: t('label.purchase.proof.company'),
      enableSorting: false
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.purchase.proof.created.at')} />
      ),
      enableSorting: true,
      cell: ({ row }) => formatPurchaseProofDate(row.original.createdAt)
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.purchase.proof.status')} />
      ),
      enableSorting: true,
      cell: ({ row }) => <PurchaseProofStatusBadge status={row.original.status} />
    }
  ];
}
